import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import type { Organisation, User } from "./types";

/**
 * Who is signed in, and which organisations they can see.
 *
 * The session is a query rather than local state, so that a sign-out in another
 * tab, a revoked session, or a suspended account all take effect on the next
 * request rather than being remembered until reload.
 */

interface SessionState {
  user: User | null;
  organisations: Organisation[];
  isLoading: boolean;
  /** True when the account exists but has not completed its second factor. */
  needsSecondFactor: boolean;
  error: unknown;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const state = useContext(SessionContext);
  if (!state) throw new Error("useSession must be used inside <SessionProvider>");
  return state;
}

/** The signed-in user, or throw. For screens behind the auth guard. */
export function useCurrentUser(): User {
  const { user } = useSession();
  if (!user) throw new Error("useCurrentUser used outside an authenticated route");
  return user;
}

export const sessionKey = ["session"] as const;
export const organisationsKey = ["organisations"] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: sessionKey,
    queryFn: ({ signal }) =>
      api.get<{ user: User; mfa_pending: boolean }>("/auth/session", undefined, signal),
    // An unauthenticated visitor is a normal state, not a failure to retry.
    retry: (count, error) =>
      !(error instanceof ApiError && (error.isUnauthenticated || error.needsSecondFactor)) && count < 2,
    staleTime: 30_000,
  });

  // A session waiting for its second factor is authenticated enough to be
  // identified, and not enough to load anything else.
  const mfaPending = Boolean(sessionQuery.data?.mfa_pending);
  const authenticated = Boolean(sessionQuery.data?.user) && !mfaPending;

  const organisationsQuery = useQuery({
    queryKey: organisationsKey,
    queryFn: ({ signal }) =>
      api.get<{ organisations: Organisation[] }>("/organisations", undefined, signal),
    enabled: authenticated,
    staleTime: 30_000,
  });

  const signOutMutation = useMutation({
    mutationFn: () => api.post<void>("/auth/sign-out"),
    onSettled: () => {
      queryClient.clear();
    },
  });

  const refresh = useCallback(async () => {
    // Cancelled first, then refetched.
    //
    // invalidateQueries alone was not enough, and the way it failed is worth
    // writing down. A request for /auth/session can already be in flight when
    // the second factor is accepted — the click, a window focus, an expired
    // staleTime — and an invalidation deduplicates onto it rather than starting
    // a new one. So `await refresh()` resolved with the answer to a question
    // asked *before* the factor was satisfied: mfa_pending still true. The
    // screen then navigated, the authenticated layout read that stale answer,
    // decided the factor was outstanding and sent the person back.
    //
    // Cancelling drops the stale request so the refetch cannot join it, and
    // refetchQueries resolves only when the new answer is in the cache.
    await queryClient.cancelQueries({ queryKey: sessionKey });
    await queryClient.refetchQueries({ queryKey: sessionKey, exact: true });
    await queryClient.invalidateQueries({ queryKey: organisationsKey });
  }, [queryClient]);

  const signOut = useCallback(async () => {
    await signOutMutation.mutateAsync();
  }, [signOutMutation]);

  const needsSecondFactor =
    mfaPending || (sessionQuery.error instanceof ApiError && sessionQuery.error.needsSecondFactor);

  const value = useMemo<SessionState>(
    () => ({
      user: mfaPending ? null : (sessionQuery.data?.user ?? null),
      organisations: organisationsQuery.data?.organisations ?? [],
      isLoading: sessionQuery.isLoading,
      needsSecondFactor,
      error:
        sessionQuery.error instanceof ApiError &&
        (sessionQuery.error.isUnauthenticated || sessionQuery.error.needsSecondFactor)
          ? null
          : sessionQuery.error,
      refresh,
      signOut,
    }),
    [
      sessionQuery.data,
      sessionQuery.isLoading,
      sessionQuery.error,
      organisationsQuery.data,
      mfaPending,
      needsSecondFactor,
      refresh,
      signOut,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
